import { http } from '@4beefopag/4bee-fopag-core/dist/services';
import moment from 'moment';
import { APONTAMENTO } from '../apontamentoProducao';
import { apiClient, getBlobApiV2 } from '../util/http';
import { ENVIADA_ID, TRANSMITINDO_ID } from '../util/globalConstant';

/** TRANSMITINDO (10) não tem opção no filtro: o rótulo é o mesmo de ENVIADA (1).
 * Sem isso, filtrar Enviada omite a folha no claim. */
const expandEnviadaFilter = (statusIds = []) => {
  if (!statusIds.includes(ENVIADA_ID) || statusIds.includes(TRANSMITINDO_ID)) {
    return statusIds;
  }
  return [...statusIds, TRANSMITINDO_ID];
};

const deserializeInvoice = (invoice) => ({
  ...invoice,
  emissao: invoice.emissao && moment(invoice.emissao),
  saida: invoice.saida && moment(invoice.saida),
  atualizadoEm: invoice.atualizadoEm && moment(invoice.atualizadoEm),
  criadoEm: invoice.criadoEm && moment(invoice.criadoEm),
  valorNf: invoice.valorNf && Number(invoice.valorNf),
});
const deserializeLog = (log) => ({
  ...log,
  insercao: log.insercao && moment(log.insercao),
  dataPrevistaFatura: log.dataPrevistaFatura && moment(log.dataPrevistaFatura),
  faturadoEm: log.faturadoEm && moment(log.faturadoEm),
  dataPagamento: log.dataPagamento && moment(log.dataPagamento),
  dataProcessamento: log.dataProcessamento && moment(log.dataProcessamento),
  criadoEm: log.criadoEm && moment(log.criadoEm),
  valorPortal: Number(log.valorPortal || 0),
  valorLiquidoFolha: Number(log.valorLiquidoFolha || 0),
});

const resolvePayrollItems = (payroll) => {
  if (payroll.items?.length) {
    return payroll.items;
  }
  const fromTipo = payroll.tipo === APONTAMENTO.tiposRequisicoes.adiantamento ? payroll.adiantamentos : payroll.mensais;
  if (fromTipo?.length) {
    return fromTipo;
  }
  if (Array.isArray(fromTipo)) {
    return fromTipo;
  }
  if (Array.isArray(payroll.items)) {
    return payroll.items;
  }
  return [];
};

const ORIGINAL_VALUE_FIELDS = ['variavel', 'reembolso', 'adiantamento', 'producao', 'descontosDiversos'];

const normalizeOriginalValues = (originalValues) => {
  if (!originalValues || typeof originalValues !== 'object') {
    return undefined;
  }

  const normalized = ORIGINAL_VALUE_FIELDS.reduce((result, field) => {
    const value = originalValues[field];
    if (value == null || value === '') {
      return result;
    }

    const numericValue = Number(value);
    if (Number.isFinite(numericValue)) {
      // eslint-disable-next-line no-param-reassign
      result[field] = numericValue;
    }

    return result;
  }, {});

  return Object.keys(normalized).length ? normalized : undefined;
};

/** API mensal items expose valorHora; UI uses hourlyRate. */
const normalizePayrollItem = (item) => {
  const normalizedItem = { ...item };
  const hourlyRate = item.valorHora ?? item.hourlyRate;

  if (hourlyRate != null) {
    const rate = Number(hourlyRate);
    normalizedItem.valorHora = item.valorHora ?? rate;
    normalizedItem.hourlyRate = rate;
  }

  const originalValues = normalizeOriginalValues(item.originalValues);
  if (originalValues) {
    normalizedItem.originalValues = originalValues;
  }

  return normalizedItem;
};

const deserializePayroll = (payroll) => ({
  ...payroll,
  totalLiquido: Number(payroll.totalLiquido || 0),
  dataPrevistaPagamentoCooperado:
    payroll.dataPrevistaPagamentoCooperado && moment(payroll.dataPrevistaPagamentoCooperado),
  dataPrevistaFatura: payroll.dataPrevistaFatura && moment(payroll.dataPrevistaFatura),
  faturadoEm: payroll.faturadoEm && moment(payroll.faturadoEm),
  criadoEm: payroll.criadoEm && moment(payroll.criadoEm),
  atualizadoEm: payroll.atualizadoEm && moment(payroll.atualizadoEm),
  latestOperation: payroll.latestOperation && {
    ...payroll.latestOperation,
    seenAt: moment(payroll.latestOperation.seenAt),
  },
  items: resolvePayrollItems(payroll).map(normalizePayrollItem),
  mensais: undefined,
  adiantamentos: undefined,
  dataAntecipacaoPagamentoCooperado:
    payroll.dataAntecipacaoPagamentoCooperado && moment(payroll.dataAntecipacaoPagamentoCooperado),
  paymentDateAdjustedByManager: payroll.paymentDateAdjustedByManager && moment(payroll.paymentDateAdjustedByManager),
  cnabPaymentDate: payroll.cnabPaymentDate && moment(payroll.cnabPaymentDate),
  dataLancamentoPedidoAntecipacaoPagamento:
    payroll.dataLancamentoPedidoAntecipacaoPagamento && moment(payroll.dataLancamentoPedidoAntecipacaoPagamento),
  logs: payroll.logs && payroll.logs.map((log) => deserializeLog(log)),
  invoicesFromNextSi:
    payroll.invoicesFromNextSi && payroll.invoicesFromNextSi.map((invoice) => deserializeInvoice(invoice)),
});

const serializeParams = (params) => {
  const {
    atualizadoEmInicial,
    atualizadoEmFinal,
    dataPagamentoCoop,
    dataPrevistaFaturaFinal,
    dataPrevistaFaturaInicial,
    folhas,
  } = params;

  return {
    ...params,
    atualizadoEmInicial: atualizadoEmInicial && atualizadoEmInicial.format('YYYY-MM-DD'),
    atualizadoEmFinal: atualizadoEmFinal && atualizadoEmFinal.format('YYYY-MM-DD'),
    dataPrevistaFaturaInicial: dataPrevistaFaturaInicial && dataPrevistaFaturaInicial.format('YYYY-MM-DD'),
    dataPrevistaFaturaFinal: dataPrevistaFaturaFinal && dataPrevistaFaturaFinal.format('YYYY-MM-DD'),
    dataPagamentoCoopInicial: dataPagamentoCoop.inicial && dataPagamentoCoop.inicial.format('YYYY-MM-DD'),
    dataPagamentoCoopFinal: dataPagamentoCoop.final && dataPagamentoCoop.final.format('YYYY-MM-DD'),
    folhas: folhas && folhas.idFolha,
    carteiras: params.carteiras.join(','),
    status: expandEnviadaFilter(params.status).join(','),
    dataPagamentoCoop: undefined,
  };
};

export const searchPayrolls = async (params) => {
  const options = { params: serializeParams(params) };
  const { data } = await apiClient.get('/payroll', options);

  return data.map((op) => deserializePayroll(op));
};

export const downloadPayrollsWorksheet = (params) =>
  getBlobApiV2('/payroll-report/summary', { params: serializeParams(params) });

export const updatePayroll = async (id, body) => {
  await apiClient.patch(`/payroll/${id}/admin-observation`, body);
};

export const cancel = (id) => apiClient.post(`/payroll/${id}/cancel`);

export const reopen = (id) => apiClient.post(`/payroll/${id}/reopen`);

export const removeById = (id) => apiClient.delete(`/payroll/${id}`);

export const requestReopen = (id, justification) => apiClient.post(`/payroll/${id}/reopen/request`, { justification });

export const rejectReopen = (id) => apiClient.post(`/payroll/${id}/reopen/reject`);

export const downloadApdataReportById = async (id) => {
  const { data } = await http.get(`/apontamento/requisicao/${id}/export/apdata`, { responseType: 'blob' });
  return data;
};

export const downloadNextSiInvoice = async (payrollId, nextSiInvoice) =>
  getBlobApiV2(`/payroll/${payrollId}/download-invoice/${nextSiInvoice}`);

export const downloadNextSiXml = async (payrollId, nextSiInvoice) =>
  getBlobApiV2(`/payroll/${payrollId}/download-xml/${nextSiInvoice}`);

export const downloadNextSiDanfe = async (payrollId, nextSiInvoice) =>
  getBlobApiV2(`/payroll/${payrollId}/download-danfe/${nextSiInvoice}`);

export const downloadSegmentoZReceipt = async (payrollId) =>
  getBlobApiV2(`/payroll/${payrollId}/download-segmentoz-receipt`);

export const findForDetail = async (id) => {
  const { data } = await apiClient.get(`/payroll/${id}/detail`, { params: { onlyPaid: true } });
  return deserializePayroll(data);
};

export const getTotalizationStatus = async (id) => {
  const { data } = await apiClient.get(`/payroll/${id}/totalization-status`);
  return data;
};

export const findById = async (id) => {
  const { data } = await apiClient.get(`/payroll/${id}`);
  return deserializePayroll(data);
};

export const refreshInvoiceById = async (id) => {
  const { data } = await apiClient.post(`/payroll/${id}/refresh/invoice`);
  return data && data.map((invoice) => deserializeInvoice(invoice));
};

export const updateVisualizationStatus = async (payrollId, { unsee }) =>
  apiClient.patch(`/payroll/${payrollId}/visualization-status`, { unsee });

export const markPayrollAsUnseen = async (payrollId) => updateVisualizationStatus(payrollId, { unsee: true });
export const markPayrollAsViewed = async (payrollId) => updateVisualizationStatus(payrollId, { unsee: false });

export const transmitToApdata = async (payrollId) => apiClient.post(`/payroll/${payrollId}/transmit`);

export const changePaymentDateTransfer = async (payrollId, body) =>
  apiClient.post(`/payroll/${payrollId}/payment/date`, body);

/**
 * Cria uma requisição do apontamento de produção
 * @param {number} folha
 * @param {Date} competencia
 * @param {"M" | "A"} tipo - A => Adiantamento, M => Mensal
 * @param {string} descricao - uma descrição breve para a requisição
 * @returns {Promise<*>}
 */
export const createPayroll = async ({ folha, competencia, tipo, descricao }) => {
  const { data } = await apiClient.post('payroll', {
    companyId: folha,
    competency: moment.utc(competencia, 'MM/YYYY').format('YYYY-MM-DD'),
    type: tipo,
    description: descricao,
  });

  return deserializePayroll(data);
};

export const sendPayroll = async ({
  expectedPaymentDate,
  expectedInvoiceDate,
  acceptedExceededCooperators,
  payroll,
}) => {
  const { data } = await apiClient.post(`/payroll/${payroll.id}/send`, {
    expectedPaymentDate,
    expectedInvoiceDate,
    acceptedExceededCooperators,
    payroll,
  });

  return deserializePayroll(data);
};

export const getPaymentStatementReport = async (payrollId) => {
  const { data } = await apiClient.get(`/payroll/${payrollId}/payment-statement`);
  return data;
};
